import { formatMoney, formatPercent } from 'helpers/format';
/**
 * Parses bonus conditions from GraphQL Object scalar (object or JSON string)
 */
export function parseBonusConditions(conditions) {
    if (conditions == null) {
        return null;
    }
    if (typeof conditions === 'string') {
        try {
            return JSON.parse(conditions);
        }
        catch (_a) {
            return null;
        }
    }
    if (typeof conditions === 'object') {
        return conditions;
    }
    return null;
}
/**
 * Serializes bonus conditions for GraphQL Object scalar input
 */
export function serializeBonusConditions(conditions) {
    if (conditions == null) {
        return null;
    }
    return JSON.stringify(conditions);
}
/**
 * Returns active bonuses sorted by order
 */
export function getActiveBonuses(bonuses) {
    return (bonuses !== null && bonuses !== void 0 ? bonuses : [])
        .filter((bonus) => bonus.active)
        .sort((a, b) => a.order - b.order);
}
/**
 * Calculates extra voucher count from QUANTITY bonuses
 */
export function calculateBonusVoucherCount(quantity, bonuses) {
    var _a;
    let extra = 0;
    for (const bonus of getActiveBonuses(bonuses)) {
        if (bonus.type !== 'QUANTITY') {
            continue;
        }
        const buyQuantity = Number((_a = parseBonusConditions(bonus.conditions)) === null || _a === void 0 ? void 0 : _a.buyQuantity);
        const bonusQuantity = Number(bonus.value);
        if (Number.isInteger(buyQuantity) &&
            buyQuantity >= 1 &&
            Number.isInteger(bonusQuantity) &&
            bonusQuantity >= 1) {
            extra += Math.floor(quantity / buyQuantity) * bonusQuantity;
        }
    }
    return extra;
}
/**
 * Total vouchers including quantity bonuses
 */
export function getEffectiveVoucherCount(quantity, bonuses) {
    return quantity + calculateBonusVoucherCount(quantity, bonuses);
}
/**
 * Label for a quantity bonus rule
 */
export function getQuantityBonusLabel(bonus) {
    var _a;
    if (bonus.type !== 'QUANTITY' || !bonus.active) {
        return null;
    }
    const buyQuantity = Number((_a = parseBonusConditions(bonus.conditions)) === null || _a === void 0 ? void 0 : _a.buyQuantity);
    const bonusQuantity = Number(bonus.value);
    if (!Number.isInteger(buyQuantity) ||
        buyQuantity < 1 ||
        !Number.isInteger(bonusQuantity) ||
        bonusQuantity < 1) {
        return null;
    }
    return `${t('Buy')} ${buyQuantity}, ${t('get')} ${bonusQuantity} ${t('free')}`;
}
/**
 * Formats bonus conditions for display in admin table
 */
export function formatBonusConditions(bonus) {
    var _a;
    if (bonus.type === 'QUANTITY') {
        const label = getQuantityBonusLabel(bonus);
        return label !== null && label !== void 0 ? label : '-';
    }
    const minQuantity = (_a = parseBonusConditions(bonus.conditions)) === null || _a === void 0 ? void 0 : _a.minQuantity;
    if (minQuantity != null) {
        return `${t('Min.')} ${minQuantity}`;
    }
    return '-';
}
/**
 * Returns the first active quantity bonus label for a sale item
 */
export function getPrimaryQuantityBonusLabel(bonuses) {
    for (const bonus of getActiveBonuses(bonuses)) {
        const label = getQuantityBonusLabel(bonus);
        if (label) {
            return label;
        }
    }
    return null;
}
/**
 * Label for an AMOUNT or PERCENTAGE bonus rule (extra value on each voucher,
 * or a voucher of its own when `separateVoucher`), with its minimum quantity
 * unless `withMinQuantity` is false
 */
export function getMonetaryBonusLabel(bonus, withMinQuantity = true) {
    var _a;
    if ((bonus.type !== 'AMOUNT' && bonus.type !== 'PERCENTAGE') ||
        !bonus.active) {
        return null;
    }
    const value = Number(bonus.value);
    if (!(value > 0)) {
        return null;
    }
    const extra = bonus.type === 'PERCENTAGE' ? formatPercent(value, 2) : formatMoney(value);
    const label = (bonus.separateVoucher
        ? t('+{{value}} in a separate voucher')
        : t('+{{value}} value on each voucher')).replace('{{value}}', extra);
    const minQuantity = Number((_a = parseBonusConditions(bonus.conditions)) === null || _a === void 0 ? void 0 : _a.minQuantity);
    if (withMinQuantity && Number.isInteger(minQuantity) && minQuantity > 1) {
        return `${label}, ${t('from {{count}} purchased').replace('{{count}}', String(minQuantity))}`;
    }
    return label;
}
/**
 * Labels of the active AMOUNT and PERCENTAGE bonuses of a sale item
 */
export function getMonetaryBonusLabels(bonuses) {
    return getActiveBonuses(bonuses)
        .map((bonus) => getMonetaryBonusLabel(bonus))
        .filter((label) => label !== null);
}
/**
 * Labels of the AMOUNT and PERCENTAGE bonuses a purchased quantity reaches,
 * without their minimum quantity (it is reached)
 */
export function getAppliedMonetaryBonusLabels(bonuses, quantity) {
    return getActiveBonuses(bonuses)
        .filter((bonus) => {
        var _a;
        const minQuantity = (_a = parseBonusConditions(bonus.conditions)) === null || _a === void 0 ? void 0 : _a.minQuantity;
        return minQuantity == null || quantity >= Number(minQuantity);
    })
        .map((bonus) => getMonetaryBonusLabel(bonus, false))
        .filter((label) => label !== null);
}
